import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Nav, Tab } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import useGuidedTour from '../../../hooks/useGuidedTour';
import {
  ADMIN_DASHBOARD_SUPER_ADMIN_INTRO_TOUR_ID,
  buildAdminDashboardSuperAdminIntroTourSteps,
} from './AdminDashboard.tour';
import { getLatestPartnerTopUps } from '../../../services/LedgerService';
import InfoPopover from '../InfoPopover';
import TopUpReveal from '../TopUpReveal';
import { clickableRowProps } from '../../../utils/clickableRow';

const UNDER_REVIEW_COPY = 'Milipot is verifying this top-up. You do not need to do anything. Do not retry.';

import { ThemeContext } from "../../../context/ThemeContext";
import AdminDashboardBalanceCardSlider from './Dashboard/AdminDashboardBalanceCardSlider';
import TopUpVaultForm from './Dashboard/TopUpVaultForm';
import TopUpUserWalletForm from './Dashboard/TopUpUserWalletForm';
import { useSelector } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPartnerVaultBalance,
  getTotalUserWalletBalance,
  getUserWalletBalances,
  getPartnerVaultTopUpCount,
  getUserWalletOutflowsByPartner,
  getTopSpenders,
} from '../../../services/AccountService';
import { getActivePartnerUserCount, getAllPartnerTeams } from '../../../services/PartnerService';
import { getExpenseTags } from '../../../services/TransactionService';
import { usePartnerHasSoloUser } from '../../../hooks/usePartnerHasSoloUser';
import { ROLE_SLUGS, PERMISSION_SLUGS, userNeedsTransactionPin } from '../../../utils/auth/permissionConstants';
import { formatPhoneForDisplay } from '../../../utils/phoneFormat';
import { buildFaqOutroSteps, SECURITY_OUTRO_TOUR_ID } from '../../../hooks/faqOutroSteps';

const ADMIN_DASHBOARD_NON_SUPER_ADMIN_OUTRO_TOUR_ID = 'admin-dashboard-non-super-admin-outro-v1';

const PERIOD_OPTIONS = [
  { label: 'All time', value: 'all' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
];

const getStartDateForPeriod = (period) => {
  if (period === 'all') return null;
  const days = { '7d': 7, '30d': 30, '90d': 90 };
  const date = new Date();
  date.setDate(date.getDate() - days[period]);
  return date.toISOString().split('T')[0];
};

const UNSETTLED_TOP_UP_STATUSES = ['pending', 'under_review'];
const UNSETTLED_ROW_POLL_CEILING_MS = 15 * 60 * 1000;

const hasFreshUnsettledRow = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const cutoff = Date.now() - UNSETTLED_ROW_POLL_CEILING_MS;
  return rows.some((row) => {
    if (!UNSETTLED_TOP_UP_STATUSES.includes(row?.status)) return false;
    const createdAt = row?.created_at ? new Date(row.created_at).getTime() : null;
    return createdAt !== null && createdAt >= cutoff;
  });
};

// Render-prop wrapper: each top-up row gets its own reveal state so a
// whole-row click opens the same details modal as the eye badge.
const TopUpRevealRow = ({ children }) => {
  const [isRevealOpen, setIsRevealOpen] = useState(false);
  return children({ isRevealOpen, setIsRevealOpen });
};

const AdminDashboard = () => {
  const { changeBackground } = useContext(ThemeContext);
  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
  }, []);

  const { authUser } = useSelector(state => state.auth);
  const partnerId = authUser?.partner_id;

  const navigate = useNavigate();

  // Solo-user partners have no vault-vs-wallet distinction to surface,
  // so this dashboard is not the right home for them. The wallet
  // dashboard owns their top-up affordance and their live top-ups feed.
  // Team partners (two or more active users) are unaffected: the hook
  // returns false and this dashboard renders exactly as before.
  const { hasSoloUser, isLoading: soloUserLoading } = usePartnerHasSoloUser(partnerId);
  useEffect(() => {
    if (!soloUserLoading && hasSoloUser) {
      navigate('/my-balance-dashboard', { replace: true });
    }
  }, [hasSoloUser, soloUserLoading, navigate]);

  const isSuperAdmin = authUser?.role_slug === ROLE_SLUGS.SUPER_ADMIN;

  const { data: setupExpenseTagsData, isLoading: setupExpenseTagsLoading } = useQuery({
    queryKey: ['expenseTagsForSetupNudge', partnerId, 1],
    queryFn: () => getExpenseTags(partnerId, 1),
    enabled: !!partnerId && isSuperAdmin,
    staleTime: 60 * 1000,
  });
  const { data: setupTeamsData, isLoading: setupTeamsLoading } = useQuery({
    queryKey: ['allPartnerTeamsForSetupNudge', partnerId],
    queryFn: () => getAllPartnerTeams(partnerId),
    enabled: !!partnerId && isSuperAdmin,
    staleTime: 60 * 1000,
  });
  const { data: setupActiveUsersCount, isLoading: setupActiveUsersCountLoading } = useQuery({
    queryKey: ['activePartnerUsersCountForSetupNudge', partnerId],
    queryFn: () => getActivePartnerUserCount(partnerId).then(res => res.data.countActiveUsers),
    enabled: !!partnerId && isSuperAdmin,
    staleTime: 60 * 1000,
  });

  const setupNudgeDataReady =
    isSuperAdmin && !setupExpenseTagsLoading && !setupTeamsLoading && !setupActiveUsersCountLoading;
  const hasAnyExpenseTag = (setupExpenseTagsData?.data?.data?.length ?? 0) > 0;
  const hasAnyTeam = (setupTeamsData?.data?.data?.length ?? 0) > 0;
  const hasAnyActiveUser = (setupActiveUsersCount ?? 0) > 0;
  const partnerNeedsSetup = !hasAnyExpenseTag || !hasAnyTeam || !hasAnyActiveUser;

  const superAdminHasWallet = !!authUser?.has_wallet;
  const superAdminIntroTourSteps = useMemo(
    () => buildAdminDashboardSuperAdminIntroTourSteps(navigate, { partnerNeedsSetup, superAdminHasWallet }),
    [navigate, partnerNeedsSetup, superAdminHasWallet]
  );
  useGuidedTour(
    ADMIN_DASHBOARD_SUPER_ADMIN_INTRO_TOUR_ID,
    superAdminIntroTourSteps,
    { enabled: isSuperAdmin && setupNudgeDataReady }
  );

  // Viewers who land here without being a super admin (a wallet-less
  // accountant, most commonly) do not need the full dashboard walk-through
  // super admins get. They do need to see the FAQ outro so its security
  // narrative funnels them to /set-transaction-pin when they still have no
  // PIN, since they can top up user wallets and every top-up asks for one.
  const needsTransactionPin = userNeedsTransactionPin(authUser);
  const nonSuperAdminOutroSteps = useMemo(
    () => buildFaqOutroSteps({
      navigate,
      hasTransactionPin: authUser?.has_transaction_pin,
      completedTourIds: authUser?.completed_tour_ids ?? [],
    }),
    [navigate, authUser?.has_transaction_pin, authUser?.completed_tour_ids]
  );
  useGuidedTour(
    ADMIN_DASHBOARD_NON_SUPER_ADMIN_OUTRO_TOUR_ID,
    nonSuperAdminOutroSteps,
    {
      enabled: !!authUser && !isSuperAdmin && needsTransactionPin,
      alsoMarkCompleted: [SECURITY_OUTRO_TOUR_ID],
    }
  );

  const queryClient = useQueryClient();

  const refreshDashboard = () => {
    queryClient.invalidateQueries({ queryKey: ['partnerVaultBalance', partnerId] });
    queryClient.invalidateQueries({ queryKey: ['totalUserWalletBalance', partnerId] });
    queryClient.invalidateQueries({ queryKey: ['partnerUserWalletBalances', partnerId] });
    queryClient.invalidateQueries({ queryKey: ['partnerVaultTopUpCount', partnerId] });
    queryClient.invalidateQueries({ queryKey: ['activePartnerUsersCount', partnerId] });
    queryClient.invalidateQueries({ queryKey: ['userWalletOutflowCount', partnerId] });
    queryClient.invalidateQueries({ queryKey: ['topSpenders', partnerId] });
    queryClient.invalidateQueries({ queryKey: ['latestPartnerTopUps', partnerId] });
  };

  const latestTopUpsRef = useRef(null);

  const scrollLatestTopUpsIntoViewOnMobile = () => {
    if (typeof window === 'undefined') return;
    const isMobileViewport = window.matchMedia('(max-width: 767.98px)').matches;
    if (!isMobileViewport) return;
    const targetElement = latestTopUpsRef.current;
    if (!targetElement) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fixedHeader = document.querySelector('.header');
    const fixedHeaderHeight = fixedHeader ? fixedHeader.getBoundingClientRect().height : 0;
    const breathingRoom = 8;
    const targetTop = targetElement.getBoundingClientRect().top + window.pageYOffset - fixedHeaderHeight - breathingRoom;
    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  const handleTopUpSubmitted = () => {
    scrollLatestTopUpsIntoViewOnMobile();
  };
  const userPermissionSlugs           = authUser?.all_permission_slugs ?? [];
  const canInitiateVaultTopUp         = userPermissionSlugs.includes(PERMISSION_SLUGS.INITIATE_VAULT_TOP_UPS);
  const canInitiateWalletDisbursement = userPermissionSlugs.includes(PERMISSION_SLUGS.INITIATE_WALLET_DISBURSEMENTS);

  const [topSpendersPeriod, setTopSpendersPeriod] = useState('all');
  const topSpendersStartDate = getStartDateForPeriod(topSpendersPeriod);

  // Piggyback on the latest top-ups poll: while an unsettled row exists
  // on that cache the balance queries refetch every four seconds so the
  // numbers move in step with the row settling. Poll stops the moment
  // nothing is in flight.
  const balanceRefetchInterval = () => {
    const cached = queryClient.getQueryData(['latestPartnerTopUps', partnerId]);
    return hasFreshUnsettledRow(cached) ? 4000 : false;
  };

  const { data: partnerVaultBalance, isFetching: partnerVaultBalanceLoading } = useQuery({
    queryKey: ['partnerVaultBalance', partnerId],
    queryFn: () => getPartnerVaultBalance(partnerId).then(res => res.data.balance),
    enabled: !!partnerId,
    staleTime: 0,
    refetchInterval: balanceRefetchInterval,
  });

  const { data: totalUserWalletBalance, isFetching: totalUserWalletBalanceLoading } = useQuery({
    queryKey: ['totalUserWalletBalance', partnerId],
    queryFn: () => getTotalUserWalletBalance(partnerId).then(res => res.data.balance),
    enabled: !!partnerId,
    staleTime: 0,
    refetchInterval: balanceRefetchInterval,
  });

  const { data: partnerUserWalletBalances, isFetching: partnerUserWalletBalancesLoading } = useQuery({
    queryKey: ['partnerUserWalletBalances', partnerId],
    queryFn: () => getUserWalletBalances(partnerId).then(res => res.data.userWalletBalances),
    enabled: !!partnerId,
    staleTime: 0,
    refetchInterval: balanceRefetchInterval,
  });

  const { data: partnerVaultTopUpCount, isFetching: partnerVaultTopUpCountLoading } = useQuery({
    queryKey: ['partnerVaultTopUpCount', partnerId],
    queryFn: () => getPartnerVaultTopUpCount(partnerId).then(res => res.data.partnerVaultTopUpCount),
    enabled: !!partnerId,
    staleTime: 0,
  });

  const { data: activePartnerUsersCount, isFetching: activePartnerUsersCountLoading } = useQuery({
    queryKey: ['activePartnerUsersCount', partnerId],
    queryFn: () => getActivePartnerUserCount(partnerId).then(res => res.data.countActiveUsers),
    enabled: !!partnerId,
    staleTime: 0,
  });

  const { data: userWalletOutflowCount, isFetching: userWalletOutflowCountLoading } = useQuery({
    queryKey: ['userWalletOutflowCount', partnerId],
    queryFn: () => getUserWalletOutflowsByPartner(partnerId).then(res => res.data.userWalletOutflowCount),
    enabled: !!partnerId,
    staleTime: 0,
  });

  const {
    data: topSpenders,
    isLoading: topSpendersLoading,
    isFetching: topSpendersFetching,
  } = useQuery({
    queryKey: ['topSpenders', partnerId, topSpendersPeriod],
    queryFn: () => getTopSpenders(partnerId, topSpendersStartDate).then(res => res.data.data),
    enabled: !!partnerId,
    staleTime: 0,
    placeholderData: (previousData) => previousData,
  });

  const { data: latestTopUps, isFetching: latestTopUpsLoading } = useQuery({
    queryKey: ['latestPartnerTopUps', partnerId],
    queryFn: () => getLatestPartnerTopUps(partnerId).then(res => res.data.data),
    enabled: !!partnerId,
    staleTime: 0,
    refetchInterval: (query) => (hasFreshUnsettledRow(query.state.data) ? 4000 : false),
  });

  const topUpStatusBadge = (topUp) => {
    switch (topUp?.status) {
      case 'successful':   return { cls: 'text-success',   label: 'Successful' };
      case 'under_review': return { cls: 'text-info',      label: 'Under Review' };
      case 'pending':      return { cls: 'text-warning',   label: 'Pending' };
      case 'failed':       return { cls: 'text-danger',    label: 'Failed' };
      default:             return { cls: 'text-secondary', label: 'Unknown' };
    }
  };

  const formatTopUpAmount = (amount) =>
    amount != null
      ? `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
      : null;

  const formatTopUpDate = (dateString) =>
    new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const partnerUserWalletsExcludingSelf = partnerUserWalletBalances?.filter(
    userData => authUser.role_slug === ROLE_SLUGS.SUPER_ADMIN || authUser.id !== userData.user_unique_id
  ) ?? [];

  const showWalletlessBanner = isSuperAdmin && setupNudgeDataReady && !superAdminHasWallet;

  // Only suppress the render once the solo check has definitively said
  // yes. While the check is loading we render the dashboard normally,
  // matching the usePartnerHasSoloUser hook's own contract that
  // isLoading means "assume the safer, more visible state so no
  // surface flashes hidden." Team partners then see the dashboard on
  // every mount without a blank flash for the round-trip of the count
  // query. If the check later resolves to solo, the useEffect above
  // navigates the user away and this render never repaints.
  if (hasSoloUser) {
    return null;
  }

  return (
    <>
      {showWalletlessBanner && (
        <div className="alert alert-warning d-flex flex-column flex-md-row align-items-md-center gap-3 mb-3" role="alert">
          <div className="flex-grow-1">
            <strong>You do not have a wallet yet.</strong>{" "}
            {hasAnyTeam
              ? "Activate your wallet to start transacting and to join a team."
              : "Create at least one team first, then come back here to activate your wallet."}
          </div>
          {hasAnyTeam ? (
            <Link to="/user-profile" className="btn btn-primary btn-sm">
              Activate wallet
            </Link>
          ) : (
            <Link to="/teams" className="btn btn-primary btn-sm">
              Go to teams
            </Link>
          )}
        </div>
      )}
      <div className="row">
        <div className="col-xl-8 order-2 order-xl-1">
          <div className="row justify-content-md-center">
            <div className="col-xl-5">
              <div className="card card-wiget card-widget-mili" data-tour="admin-dashboard-vault-balance">
                <div className="card-body">
                  <div className="card-wiget-info">
                    <h4 className="count-num text-center">
                      {partnerVaultBalanceLoading ? (
                        <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
                      ) : (
                        <p>{partnerVaultBalance}</p>
                      )}
                    </h4>
                    <p className='text-center'>Partner Vault Balance</p>
                    <button
                      type="button"
                      className="position-absolute top-0 end-0 m-2 d-flex align-items-center justify-content-center"
                      onClick={() => refreshDashboard()}
                      title="Refresh Dashboard"
                      data-tour="admin-dashboard-refresh"
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: '2px solid #198754',
                        color: '#198754',
                        background: 'white',
                        padding: 0,
                      }}
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-12" data-tour="admin-dashboard-stats">
              <AdminDashboardBalanceCardSlider
                partnerVaultTopUpCountLoading={partnerVaultTopUpCountLoading}
                partnerVaultTopUpCount={partnerVaultTopUpCount}
                activePartnerUsersCount={activePartnerUsersCount}
                activePartnerUsersCountLoading={activePartnerUsersCountLoading}
                userWalletOutflowCount={userWalletOutflowCount}
                userWalletOutflowCountLoading={userWalletOutflowCountLoading}
                totalUserWalletBalance={totalUserWalletBalance}
                totalUserWalletBalanceLoading={totalUserWalletBalanceLoading}
              />
            </div>

            <div className="col-12" data-tour="admin-dashboard-current-balances">
              <div className="card">
                <div className="card-header d-block">
                  <h4 className="card-title mb-0">Current Balances</h4>
                </div>
                <div className="card-body p-0">
                  {partnerUserWalletBalancesLoading ? (
                    <div className="d-flex justify-content-center py-4">
                      <span className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  ) : !partnerUserWalletBalances || partnerUserWalletBalances.length === 0 ? (
                    <p className="text-center text-muted py-4 mb-0">No user wallet balances yet.</p>
                  ) : (
                    <>
                      <div className="table-responsive px-3 pt-2">
                        <table className="table verticle-middle mb-0">
                          <thead>
                            <tr>
                              <th scope="col">User</th>
                              <th scope="col" className="text-end">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {partnerUserWalletBalances.slice(0, 5).map((user) => (
                              <tr key={user.user_unique_id}>
                                <td style={{ overflowWrap: 'anywhere' }}>{user.userName}</td>
                                <td className="text-end text-nowrap">{user.balance}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {partnerUserWalletBalances.length > 5 && (
                        <div className="text-end mt-2 me-3 mb-2">
                          <Link to="/vault-dashboard/user-wallet-balances" className="btn btn-link btn-sm p-0">
                            View all ({partnerUserWalletBalances.length})
                          </Link>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="col-12" data-tour="admin-dashboard-top-spenders">
              <div className="card">
                <div className="card-header d-block">
                  <div className="d-flex align-items-center">
                    <h4 className="card-title mb-0">Top Spenders</h4>
                    {topSpendersFetching && !topSpendersLoading && (
                      <span className="spinner-border spinner-border-sm text-primary ms-2" role="status" />
                    )}
                  </div>
                </div>
                <div className="card-body p-0">
                  {topSpendersLoading ? (
                    <div className="d-flex justify-content-center py-4">
                      <span className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  ) : !topSpenders || topSpenders.length === 0 ? (
                    <p className="text-center text-muted py-4 mb-0">No spending in this period yet.</p>
                  ) : (
                    <div className="table-responsive px-3 pt-2">
                      <table className="table verticle-middle mb-0">
                        <thead>
                          <tr>
                            <th scope="col">User</th>
                            <th scope="col" className="text-end">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topSpenders.map((spender, i) => (
                            <tr key={i}>
                              <td style={{ overflowWrap: 'anywhere' }}>{spender.userName}</td>
                              <td className="text-end text-nowrap">KES {spender.amount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="d-flex gap-2 mt-3 px-3 pb-3">
                    {PERIOD_OPTIONS.map(({ label, value }) => (
                      <button
                        key={value}
                        type="button"
                        className={`btn btn-sm ${topSpendersPeriod === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setTopSpendersPeriod(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12" ref={latestTopUpsRef} data-tour="admin-dashboard-latest-top-ups">
              <div className="card shadow-sm border-0" style={{ scrollMarginTop: '5rem' }}>
                <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                  <h2 className="heading h5 mb-0 fw-bold text-secondary">Latest Top Ups</h2>
                </div>
                <div className="card-body p-0">
                  {latestTopUpsLoading ? (
                    <div className="d-flex justify-content-center py-4">
                      <span className="spinner-border spinner-border-sm text-primary" role="status" />
                    </div>
                  ) : latestTopUps?.length > 0 ? (
                    <>
                      <div className="table-responsive d-none d-xl-block px-3 pt-2">
                        <table className="table verticle-middle mb-0">
                          <thead>
                            <tr>
                              <th scope="col">Receipt</th>
                              <th scope="col">Amount</th>
                              <th scope="col">Source</th>
                              <th scope="col">Status</th>
                              <th scope="col" className="text-end">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {latestTopUps.map((item) => {
                              const { cls, label } = topUpStatusBadge(item);
                              return (
                                <TopUpRevealRow key={item.id}>
                                  {({ isRevealOpen, setIsRevealOpen }) => (
                                    <tr {...clickableRowProps(() => setIsRevealOpen(true))}>
                                      <td className="text-muted" style={{ overflowWrap: 'anywhere' }}>
                                        {item.post_mpesa_receipt_number ? (
                                          <span className="d-inline-flex align-items-center gap-2">
                                            <span>{item.post_mpesa_receipt_number}</span>
                                            <TopUpReveal topUp={item} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                          </span>
                                        ) : (
                                          <TopUpReveal topUp={item} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                        )}
                                      </td>
                                      <td className="fw-medium text-dark">
                                        {item.amount != null
                                          ? formatTopUpAmount(item.amount)
                                          : <span className="text-muted fst-italic">Pending</span>}
                                      </td>
                                      <td className="text-muted" style={{ overflowWrap: 'anywhere' }}>
                                        {item.source_phone_number ? formatPhoneForDisplay(item.source_phone_number) : '-'}
                                      </td>
                                      <td className={`fw-medium ${cls}`}>
                                        {label}
                                        {item.status === 'under_review' && (
                                          <InfoPopover
                                            id={`dashboard-top-up-under-review-${item.id}`}
                                            body={UNDER_REVIEW_COPY}
                                            ariaLabel="What does Under Review mean?"
                                          />
                                        )}
                                      </td>
                                      <td className="text-end text-muted small">
                                        {formatTopUpDate(item.post_mpesa_transaction_date ?? item.created_at)}
                                      </td>
                                    </tr>
                                  )}
                                </TopUpRevealRow>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                        <table className="table verticle-middle mb-0">
                          <thead>
                            <tr>
                              <th scope="col">Amount</th>
                              <th scope="col">Source / Receipt</th>
                              <th scope="col">Status</th>
                              <th scope="col" className="text-end">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {latestTopUps.map((item) => {
                              const { cls, label } = topUpStatusBadge(item);
                              return (
                                <TopUpRevealRow key={item.id}>
                                  {({ isRevealOpen, setIsRevealOpen }) => (
                                    <tr {...clickableRowProps(() => setIsRevealOpen(true))}>
                                      <td className="fw-medium text-dark">
                                        <span className="d-inline-flex align-items-center gap-2">
                                          <span>
                                            {item.amount != null
                                              ? formatTopUpAmount(item.amount)
                                              : <span className="text-muted fst-italic">Pending</span>}
                                          </span>
                                          <TopUpReveal topUp={item} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                        </span>
                                      </td>
                                      <td className="text-muted" style={{ overflowWrap: 'anywhere' }}>
                                        {item.source_phone_number || item.post_mpesa_receipt_number ? (
                                          <>
                                            {item.source_phone_number && <div>{formatPhoneForDisplay(item.source_phone_number)}</div>}
                                            {item.post_mpesa_receipt_number && (
                                              <div className="small">{item.post_mpesa_receipt_number}</div>
                                            )}
                                          </>
                                        ) : (
                                          <span className="fst-italic small">Awaiting M-Pesa confirmation</span>
                                        )}
                                      </td>
                                      <td className={`fw-medium ${cls}`}>
                                        {label}
                                        {item.status === 'under_review' && (
                                          <InfoPopover
                                            id={`dashboard-top-up-under-review-md-${item.id}`}
                                            body={UNDER_REVIEW_COPY}
                                            ariaLabel="What does Under Review mean?"
                                          />
                                        )}
                                      </td>
                                      <td className="text-end text-muted small">
                                        {formatTopUpDate(item.post_mpesa_transaction_date ?? item.created_at)}
                                      </td>
                                    </tr>
                                  )}
                                </TopUpRevealRow>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <ul className="list-group list-group-flush d-block d-md-none">
                        {latestTopUps.map((item) => {
                          const { cls, label } = topUpStatusBadge(item);
                          const hasReference = item.source_phone_number || item.post_mpesa_receipt_number;
                          return (
                            <TopUpRevealRow key={item.id}>
                              {({ isRevealOpen, setIsRevealOpen }) => (
                                <li
                                  className="list-group-item py-3"
                                  {...clickableRowProps(() => setIsRevealOpen(true))}
                                >
                                  <div className="d-flex gap-3">
                                    <div
                                      className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${cls}`}
                                      style={{ width: 40, height: 40, background: 'rgba(108, 117, 125, 0.08)' }}
                                    >
                                      <i className="bi bi-arrow-down-circle" style={{ fontSize: '1.25rem' }} />
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                      <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                        <span className="fw-semibold text-body d-inline-flex align-items-center gap-2">
                                          <span>
                                            {item.amount != null
                                              ? formatTopUpAmount(item.amount)
                                              : <span className="text-muted fst-italic">Pending</span>}
                                          </span>
                                          <TopUpReveal topUp={item} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                        </span>
                                        <span className={`fw-medium ${cls} text-nowrap small`}>
                                          {label}
                                          {item.status === 'under_review' && (
                                            <InfoPopover
                                              id={`dashboard-top-up-under-review-sm-${item.id}`}
                                              body={UNDER_REVIEW_COPY}
                                              ariaLabel="What does Under Review mean?"
                                            />
                                          )}
                                        </span>
                                      </div>
                                      <div className="small text-muted mb-1">
                                        {formatTopUpDate(item.post_mpesa_transaction_date ?? item.created_at)}
                                      </div>
                                      {hasReference ? (
                                        <>
                                          {item.source_phone_number && (
                                            <div className="small text-muted" style={{ overflowWrap: 'anywhere' }}>
                                              From {formatPhoneForDisplay(item.source_phone_number)}
                                            </div>
                                          )}
                                          {item.post_mpesa_receipt_number && (
                                            <div className="small text-muted" style={{ overflowWrap: 'anywhere' }}>
                                              Receipt {item.post_mpesa_receipt_number}
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div className="small text-muted fst-italic">
                                          Awaiting M-Pesa confirmation
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              )}
                            </TopUpRevealRow>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <p className="text-center text-muted py-4 mb-0">No top-ups recorded yet.</p>
                  )}
                </div>
                <div className="text-end px-3 py-2 border-top">
                  <Link to="/ledger?tab=top-ups" className="btn btn-link btn-sm p-0">
                    View more
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>

        {(canInitiateVaultTopUp || canInitiateWalletDisbursement) && (
          <div className="col-xl-4 order-1 order-xl-2" data-tour="admin-dashboard-top-up-form" style={{ scrollMarginTop: '5rem' }}>
            <div className="row">
              <div className="col-xl-12">
                <div className="card h-auto">
                  <div className="card-body px-0 pt-1">
                    {canInitiateVaultTopUp && canInitiateWalletDisbursement ? (
                      <Tab.Container defaultActiveKey="NavTopUpVault">
                        <div className="buy-sell">
                          <Nav className="nav nav-tabs" id="nav-tab2" role="tablist" style={{ textTransform: 'uppercase' }}>
                            <Nav.Link as="button" className="nav-link" eventKey="NavTopUpVault" type="button" style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '0.03em' }}>Top up Vault</Nav.Link>
                            <Nav.Link as="button" className="nav-link" eventKey="NavTopUpUserWallet" type="button" data-tour="admin-dashboard-top-up-user-wallet-tab" style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '0.03em' }}>Top up user</Nav.Link>
                          </Nav>
                        </div>
                        <Tab.Content>
                          <Tab.Pane eventKey="NavTopUpVault">
                            <div className="sell-element">
                              <TopUpVaultForm partnerId={partnerId} onTopUpSubmitted={handleTopUpSubmitted} />
                            </div>
                          </Tab.Pane>
                          <Tab.Pane eventKey="NavTopUpUserWallet">
                            <div className="sell-element">
                              <TopUpUserWalletForm
                                partnerId={partnerId}
                                partnerUserWalletBalances={partnerUserWalletsExcludingSelf}
                                partnerUserWalletBalancesLoading={partnerUserWalletBalancesLoading}
                              />
                            </div>
                          </Tab.Pane>
                        </Tab.Content>
                      </Tab.Container>
                    ) : canInitiateVaultTopUp ? (
                      <div className="sell-element">
                        <TopUpVaultForm partnerId={partnerId} onTopUpSubmitted={handleTopUpSubmitted} />
                      </div>
                    ) : (
                      <div className="sell-element">
                        <TopUpUserWalletForm
                          partnerId={partnerId}
                          partnerUserWalletBalances={partnerUserWalletsExcludingSelf}
                          partnerUserWalletBalancesLoading={partnerUserWalletBalancesLoading}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default AdminDashboard;
